import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Search } from 'lucide-react'; 
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

export default function Header() {
  const { t, i18n } = useTranslation(); 
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null); 
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false); 
  
  const searchInputRef = useRef(null); 
  const mobileMenuRef = useRef(null);

  const deepGreen = '#656D4A';
  const darkOliveText = '#3D422C';
  const creamBackground = '#FDF6E3';
  const burgundyRed = '#800020';

  const categories = [
    { name: t('categories.learn-discover'), path: '/category/learn-discover' },
    { name: t('categories.region-grape'), path: '/category/region-grape' },
    { name: t('categories.foodpairing-lifestyle'), path: '/category/foodpairing-lifestyle' },
    { name: t('categories.health-beauty'), path: '/category/health-beauty' },
  ];

  // Get canonical URL
  const getCanonicalUrl = () => {
    return `${window.location.origin}${location.pathname}`;
  };

  // Scroll control for header
  useEffect(() => {
    const controlHeader = () => {
      if (typeof window !== 'undefined') {
        if (window.scrollY > lastScrollY && window.scrollY > 100) {
          setIsVisible(false);
          setIsMobileMenuOpen(false); 
          setIsSearchOpen(false); 
        } else {
          setIsVisible(true);
        }
        setLastScrollY(window.scrollY);
      }
    };
    window.addEventListener('scroll', controlHeader, { passive: true });
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsMobileMenuOpen(false);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleMobileCategoryToggle = () => {
    setIsCategoryOpen(!isCategoryOpen);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const Separator = () => <span className="text-white/20 mx-4 hidden lg:inline" aria-hidden="true">|</span>;

  // ===== SCHEMA 3: useMemo for Organization Schema =====
  const organizationSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": t('brand.title'),
    "alternateName": i18n.language === 'zh' ? '欧洲葡萄酒之旅' : 'Euro Wine Trip',
    "url": typeof window !== 'undefined' ? window.location.origin : '',
    "logo": typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : '/logo.png',
    "description": t('brand.description'),
    "sameAs": [
      // TODO: Add actual social media links when available
      // "https://facebook.com/yourpage",
      // "https://twitter.com/yourpage",
      // "https://instagram.com/yourpage",
      // "https://pinterest.com/yourpage"
    ].filter(Boolean),
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "eurowinetrip2026@foxmail.com",
      "availableLanguage": ["English", "Chinese"]
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "CN"
    }
  }), [t, i18n.language]); // Only recalculate when translation or language changes

  // Inject structured data
  useEffect(() => {
    // Guard against server-side rendering
    if (typeof document === 'undefined') return;
    
    // Remove old script if exists
    const oldScript = document.getElementById('organization-schema');
    if (oldScript) {
      oldScript.remove();
    }
    
    // Create new script
    const script = document.createElement('script');
    script.id = 'organization-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(organizationSchema);
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('organization-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [organizationSchema]); // Dependency on memoized schema

  return (
    <>
      {/* SEO Meta Data */}
      <Helmet>
        <html lang={i18n.language} />
        <link rel="canonical" href={getCanonicalUrl()} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="format-detection" content="telephone=no" />
        {i18n.language === 'zh' ? (
          <meta name="description" content="探索欧洲葡萄酒文化、产区知识、美食搭配和健康生活。专业的葡萄酒资讯平台。" />
        ) : (
          <meta name="description" content="Explore European wine culture, regions, food pairing, and healthy lifestyle. Your trusted wine information platform." />
        )}
      </Helmet>

      <header 
        role="banner"
        itemScope 
        itemType="https://schema.org/WPHeader"
        className={`w-full fixed top-0 left-0 z-[100] shadow-xl font-serif transition-transform duration-500 ease-in-out ${
          isVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{ backgroundColor: deepGreen }}
      >
        {/* Skip navigation link - for accessibility and SEO */}
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white text-[#656D4A] p-4 z-[200] rounded-lg shadow-lg"
        >
          {i18n.language === 'zh' ? '跳转到主要内容' : 'Skip to main content'}
        </a>

        <div className="mx-auto pl-0 md:pl-0 pr-4 md:pr-12">
          <div className="flex justify-between items-center h-20 md:h-24">
            
            {/* LOGO & BRANDING - Enhanced SEO */}
            <Link 
              to="/" 
              className="flex items-center group h-20 md:h-24" 
              aria-label={t('brand.title')}
              itemProp="url"
            >
              <div className="h-full flex items-center justify-center shrink-0 overflow-hidden">
                <img 
                  src="/logo.png" 
                  alt={i18n.language === 'zh' ? '欧洲葡萄酒之旅 - 首页' : 'Euro Wine Trip - Home'}
                  className="h-full w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                  itemProp="logo"
                  decoding="async"
                  loading="eager"
                  width="200"
                  height="56"
                />
              </div>
              <span 
                itemProp="name" 
                className="text-xl md:text-3xl text-white italic tracking-tight whitespace-nowrap font-bold"
                style={{ 
                  color: '#D4AF37',
                  textShadow: '-1px -1px 1px rgba(0,0,0,0.7), 1px 1px 1px rgba(255,255,255,0.2)'
                }}
              >
                {t('brand.title')}
              </span>
            </Link>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden md:flex items-center" aria-label={i18n.language === 'zh' ? '主导航' : 'Main Navigation'}>
              <Link 
                to="/" 
                aria-current={isActivePath('/') ? 'page' : undefined}
                className={`text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                  isActivePath('/') ? 'text-[#800020] font-bold' : 'text-white/80 hover:text-[#800020]'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Separator />
              
              <div className="relative group h-20 md:h-24 flex items-center">
                <button 
                  aria-haspopup="true" 
                  aria-expanded="false"
                  aria-label={i18n.language === 'zh' ? '文章分类' : 'Categories'}
                  className={`flex items-center gap-1 text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                    categories.some(cat => isActivePath(cat.path)) ? 'text-[#800020] font-bold' : 'text-white/80 group-hover:text-[#800020]'
                  }`}
                >
                  {t('nav.category')} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" aria-hidden="true" />
                </button>
                
                <ul 
                  className="absolute top-full right-0 w-64 shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-[110] rounded-b-2xl overflow-hidden list-none"
                  style={{ backgroundColor: creamBackground }}
                  role="menu"
                  aria-label={i18n.language === 'zh' ? '分类菜单' : 'Category menu'}
                >
                  <div className="py-2">
                    {categories.map((cat) => (
                      <li key={cat.path} role="none">
                        <Link 
                          to={cat.path} 
                          role="menuitem"
                          onMouseEnter={() => setHoveredIndex(cat.name)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          className="block px-6 py-4 text-sm font-sans font-semibold transition-all duration-200"
                          style={{ 
                            color: hoveredIndex === cat.name || isActivePath(cat.path) ? burgundyRed : darkOliveText,
                            backgroundColor: isActivePath(cat.path) ? 'rgba(128, 0, 32, 0.05)' : 'transparent'
                          }}
                          aria-current={isActivePath(cat.path) ? 'page' : undefined}
                        >
                          {cat.name}
                        </Link>
                      </li>
                    ))}
                  </div>
                </ul>
              </div>

              <Separator />
              <Link 
                to="/contact" 
                aria-current={isActivePath('/contact') ? 'page' : undefined}
                className={`text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                  isActivePath('/contact') ? 'text-[#800020] font-bold' : 'text-white/80 hover:text-[#800020]'
                }`}
              >
                {t('nav.about')}
              </Link>
              
              <Separator />

              {/* EXPANDING SEARCH - Enhanced for SEO */}
              <div className="flex items-center relative" role="search">
                <div 
                  className={`flex items-center transition-all duration-500 ease-in-out overflow-hidden rounded-full ${
                    isSearchOpen 
                      ? 'w-48 md:w-64 bg-white px-4 py-1.5 mr-2 shadow-md' 
                      : 'w-0 opacity-0 pointer-events-none'
                  }`}
                >
                  <label htmlFor="search-input-desktop" className="sr-only">
                    {i18n.language === 'zh' ? '搜索文章' : 'Search articles'}
                  </label>
                  <input
                    id="search-input-desktop"
                    ref={searchInputRef}
                    type="search"
                    placeholder={t('nav.search_placeholder')}
                    className="bg-transparent text-[#3D422C] text-sm outline-none w-full placeholder:text-olive/40 font-sans"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchSubmit}
                    aria-label={i18n.language === 'zh' ? '搜索葡萄酒文章和指南' : 'Search wine articles and guides'}
                  />
                </div>
                <button 
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="text-white/80 hover:text-[#800020] transition-colors duration-300"
                  aria-label={isSearchOpen 
                    ? (i18n.language === 'zh' ? '关闭搜索' : 'Close search') 
                    : (i18n.language === 'zh' ? '打开搜索' : 'Open search')}
                  aria-expanded={isSearchOpen}
                >
                  {isSearchOpen ? <X size={20} /> : <Search size={20} strokeWidth={2.5} />}
                </button>
              </div>
              
              <div className="flex items-center gap-6 ml-6">
                <button 
                  onClick={toggleLanguage} 
                  aria-label={i18n.language === 'en' ? '切换到中文' : 'Switch to English'}
                  className="text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full border border-white/20 hover:bg-white/10 transition-all"
                >
                  {i18n.language === 'en' ? '中文' : 'EN'}
                </button>
              </div>
            </nav>

            {/* MOBILE TRIGGER */}
            <div className="md:hidden flex items-center gap-4">
              <button 
                onClick={toggleLanguage} 
                className="text-white text-[14px] font-bold tracking-widest uppercase px-4 py-2 rounded-full border border-white/30 bg-white/10"
                aria-label={i18n.language === 'en' ? '切换到中文' : 'Switch to English'}
              >
                {i18n.language === 'en' ? '中文' : 'EN'}
              </button>
              <button 
                onClick={() => setIsMobileMenuOpen(true)} 
                className="text-white p-1 hover:text-[#D4AF37] transition-colors"
                aria-label={i18n.language === 'zh' ? '打开菜单' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                <Menu size={32} />
              </button>
            </div>
          </div>
        </div>

        {/* MOBILE MENU PANEL */}
        {isMobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/40 z-[120] backdrop-blur-[2px] md:hidden transition-opacity duration-300" 
              onClick={() => setIsMobileMenuOpen(false)} 
              aria-hidden="true" 
            />
            <aside 
              ref={mobileMenuRef}
              className={`fixed top-0 right-0 w-80 shadow-2xl transform transition-transform duration-500 ease-in-out md:hidden z-[130] flex flex-col rounded-bl-[3.5rem] overflow-hidden ${
                isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
              }`} 
              style={{ backgroundColor: creamBackground, height: 'auto', maxHeight: '90vh' }}
              aria-label={i18n.language === 'zh' ? '移动端菜单' : 'Mobile menu'}
            >
              <div className="h-16 flex items-center justify-between px-6 shrink-0" style={{ backgroundColor: deepGreen }}>
                <div className="flex items-center gap-2 text-white italic font-serif text-lg">
                  <img src="/logo.png" alt="" className="w-6 h-6 object-contain" aria-hidden="true" /> 
                  {i18n.language === 'en' ? 'Menu' : '菜单'}
                </div>
                <button 
                  onClick={() => setIsMobileMenuOpen(false)} 
                  className="text-white hover:text-[#D4AF37] transition-colors" 
                  aria-label={i18n.language === 'zh' ? '关闭菜单' : 'Close menu'}
                >
                  <X size={28} />
                </button>
              </div>

              <nav className="p-6 flex flex-col gap-5 overflow-y-auto" aria-label={i18n.language === 'zh' ? '移动导航' : 'Mobile Navigation'}>
                {/* Mobile Search */}
                <div className="relative mb-2" role="search">
                  <label htmlFor="search-input-mobile" className="sr-only">
                    {i18n.language === 'zh' ? '搜索文章' : 'Search articles'}
                  </label>
                  <div className="flex items-center bg-white border border-black/10 rounded-full px-4 py-2">
                    <Search size={14} className="text-black/40 mr-2" aria-hidden="true" />
                    <input 
                      id="search-input-mobile"
                      type="search"
                      placeholder={t('nav.search_placeholder')}
                      className="bg-transparent outline-none text-sm w-full font-sans text-[#3D422C]"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchSubmit}
                      aria-label={i18n.language === 'zh' ? '搜索葡萄酒文章和指南' : 'Search wine articles and guides'}
                    />
                  </div>
                </div>

                {/* Mobile Navigation Links */}
                <Link 
                  to="/" 
                  aria-current={isActivePath('/') ? 'page' : undefined}
                  className={`text-xl font-bold tracking-widest p-3 transition-colors ${
                    isActivePath('/') ? 'text-[#800020]' : 'text-[#3D422C] hover:text-[#800020]'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.home')}
                </Link>
                
                <div className="flex flex-col">
                  <button 
                    onClick={handleMobileCategoryToggle} 
                    aria-expanded={isCategoryOpen}
                    aria-controls="mobile-category-menu"
                    className={`text-xl font-bold tracking-widest flex justify-between items-center p-3 transition-colors ${
                      categories.some(cat => isActivePath(cat.path)) ? 'text-[#800020]' : 'text-[#3D422C]'
                    }`}
                  >
                    {t('nav.category')} 
                    <ChevronDown 
                      className={`transition-transform duration-300 ${isCategoryOpen ? 'rotate-180' : ''}`} 
                      size={20} 
                      aria-hidden="true" 
                    />
                  </button>
                  
                  {/* Category Dropdown with Animation */}
                  <div 
                    id="mobile-category-menu"
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isCategoryOpen ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <ul className="flex flex-col gap-1 pl-4 border-l border-[#656D4A]/30 list-none">
                      {categories.map((cat) => (
                        <li key={cat.path}>
                          <Link 
                            to={cat.path} 
                            className={`text-[16px] font-medium p-3 transition-colors block ${
                              isActivePath(cat.path) 
                                ? 'text-[#800020] bg-[#800020]/5 rounded-lg' 
                                : 'text-[#3D422C]/90 hover:text-[#800020]'
                            }`} 
                            onClick={() => setIsMobileMenuOpen(false)}
                            aria-current={isActivePath(cat.path) ? 'page' : undefined}
                          >
                            {cat.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <Link 
                  to="/contact" 
                  aria-current={isActivePath('/contact') ? 'page' : undefined}
                  className={`text-xl font-bold tracking-widest p-3 mb-4 transition-colors ${
                    isActivePath('/contact') ? 'text-[#800020]' : 'text-[#3D422C] hover:text-[#800020]'
                  }`} 
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.about')}
                </Link>
              </nav>
            </aside>
          </>
        )}
      </header>
      
      {/* Spacer div to prevent content from hiding under fixed header */}
      <div className="h-5 md:h-6" aria-hidden="true" />
    </>
  );
}